import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Typography,
  Box,
  Paper,
  Button,
  Grid,
  Chip,
  CircularProgress,
  Tabs,
  Tab,
  Avatar,
  LinearProgress,
  FormControlLabel,
  Checkbox,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Autocomplete,
} from '@mui/material';
import {
  PlayArrow as StartIcon,
  Pause as PauseIcon,
  Stop as StopIcon,
  Visibility as MonitorIcon,
  PresentToAll as PresentToAllIcon,
  Campaign as BroadcastIcon,
  EmailOutlined as EmailIcon,
  Assessment as AnalyticsIcon,
  PersonAdd as AddStudentIcon,
  PersonAddAlt1 as PersonAddIcon,
  Download as ExportIcon,
  VpnKey as CredentialsIcon,
  DeleteOutline as DeleteIcon,
  Schedule as ScheduleIcon,
  Timer as TimerIcon,
  FiberManualRecord as DotIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { sessionService } from '../../services/session.service';
import { progressService } from '../../services/progress.service';
import { socketService } from '../../services/socket.service';
import { SessionDetail, SessionStudentInfo, AddStudentInput } from '../../types/session.types';
import { StudentOverview } from '../../types/progress.types';
import { useSessionSocket } from '../../hooks/useSessionSocket';
import { useNotification } from '../../context/NotificationContext';
import { useAuth } from '../../hooks/useAuth';
import { browserNotifications } from '../../utils/browserNotifications';

const statusConfig: Record<string, { label: string; color: 'default' | 'success' | 'warning' | 'error' | 'info' }> = {
  scheduled: { label: 'SCHEDULED', color: 'info' },
  preparing: { label: 'PROVISIONING', color: 'warning' },
  ready: { label: 'READY', color: 'success' },
  active: { label: 'SESSION ACTIVE', color: 'success' },
  completed: { label: 'COMPLETED', color: 'default' },
};

interface InstructorOption {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

const SessionActive: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { user } = useAuth();
  const showNotificationRef = useRef(showNotification);
  showNotificationRef.current = showNotification;

  const [session, setSession] = useState<SessionDetail | null>(null);
  const [students, setStudents] = useState<SessionStudentInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [endDialogOpen, setEndDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [preparing, setPreparing] = useState(false);

  // Email invitation state
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [sendingInvitations, setSendingInvitations] = useState(false);

  // Desktop cleanup state
  const [desktopStatuses, setDesktopStatuses] = useState<any[]>([]);
  const [cleanupDialogOpen, setCleanupDialogOpen] = useState(false);
  const [cleaningUp, setCleaningUp] = useState(false);

  // FL10: Student progress overview
  const [overview, setOverview] = useState<Map<string, StudentOverview>>(new Map());

  // FL11: Action dialog states
  const [broadcastDialogOpen, setBroadcastDialogOpen] = useState(false);
  const [broadcastMsg, setBroadcastMsg] = useState('');
  const [addStudentsDialogOpen, setAddStudentsDialogOpen] = useState(false);
  const [newStudentName, setNewStudentName] = useState('');
  const [newStudentEmail, setNewStudentEmail] = useState('');
  const [newStudentTenantUrl, setNewStudentTenantUrl] = useState('');
  const [newStudentTenantAdminEmail, setNewStudentTenantAdminEmail] = useState('');
  const [newStudentTenantAdminPassword, setNewStudentTenantAdminPassword] = useState('');
  const [newStudentSharingPortalUrl, setNewStudentSharingPortalUrl] = useState('');
  const [newStudentSkytapPortalPassword, setNewStudentSkytapPortalPassword] = useState('');
  const [addingStudent, setAddingStudent] = useState(false);
  const [resetCredDialogOpen, setResetCredDialogOpen] = useState(false);
  const [selectedStudentForReset, setSelectedStudentForReset] = useState<SessionStudentInfo | null>(null);
  const [newTenantUrl, setNewTenantUrl] = useState('');
  const [newTenantAdminEmail, setNewTenantAdminEmail] = useState('');
  const [newTenantAdminPassword, setNewTenantAdminPassword] = useState('');
  const [newSharingPortalUrl, setNewSharingPortalUrl] = useState('');
  const [newSkytapPortalPassword, setNewSkytapPortalPassword] = useState('');

  // WebSocket connection for broadcast
  const { connectionStatus } = useSessionSocket({ sessionId: id || '' });

  // Settings state
  const [settingsForm, setSettingsForm] = useState({
    enableProgressTracking: true,
    allowHelpRequests: true,
    sendReminders: true,
    recordSession: false,
  });
  const [savingSettings, setSavingSettings] = useState(false);

  // Co-instructor state
  const [addCoInstructorDialogOpen, setAddCoInstructorDialogOpen] = useState(false);
  const [availableInstructors, setAvailableInstructors] = useState<InstructorOption[]>([]);
  const [selectedNewCoInstructor, setSelectedNewCoInstructor] = useState<InstructorOption | null>(null);
  const [addingCoInstructor, setAddingCoInstructor] = useState(false);

  const loadData = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const [sess, studs] = await Promise.all([
        sessionService.getSession(id),
        sessionService.getStudents(id),
      ]);
      setSession(sess);
      setStudents(studs);
      // FL10: Fetch student progress overview
      try {
        const overviewData = await progressService.getSessionOverview(id);
        setOverview(new Map(overviewData.map((o) => [o.studentId, o])));
      } catch {
        // non-critical — overview may not be available for scheduled sessions
      }
      setSettingsForm({
        enableProgressTracking: sess.enableProgressTracking,
        allowHelpRequests: sess.allowHelpRequests,
        sendReminders: sess.sendReminders,
        recordSession: sess.recordSession,
      });
    } catch {
      showNotificationRef.current('Failed to load session', 'error');
      navigate('/instructor/sessions');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Request browser notification permission for help request alerts
  useEffect(() => {
    browserNotifications.requestPermission();
  }, []);

  // Fetch desktop statuses when session is completed
  useEffect(() => {
    if (!id || !session || session.status !== 'completed') return;
    sessionService.getDesktopStatus(id)
      .then(setDesktopStatuses)
      .catch(() => { /* non-critical */ });
  }, [id, session?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCleanup = async () => {
    if (!id) return;
    setCleanupDialogOpen(false);
    setCleaningUp(true);
    try {
      await sessionService.cleanupDesktops(id);
      showNotification('Desktops cleaned up', 'success');
      // Refresh desktop statuses
      const updated = await sessionService.getDesktopStatus(id);
      setDesktopStatuses(updated);
    } catch {
      showNotification('Failed to clean up desktops', 'error');
    } finally {
      setCleaningUp(false);
    }
  };

  const handlePrepare = async () => {
    if (!id) return;
    setPreparing(true);
    try {
      await sessionService.prepareSession(id);
      showNotification('Desktops provisioned — session is ready to start!', 'success');
      await loadData();
    } catch {
      showNotification('Failed to provision desktops. You can retry.', 'error');
      await loadData();
    } finally {
      setPreparing(false);
    }
  };

  const handleStart = async () => {
    if (!id) return;
    try {
      await sessionService.startSession(id);
      showNotification('Session started!', 'success');
      await loadData();
    } catch {
      showNotification('Failed to start session', 'error');
    }
  };

  const handlePause = async () => {
    if (!id) return;
    try {
      await sessionService.pauseSession(id);
      showNotification('Session paused', 'info');
      await loadData();
    } catch {
      showNotification('Failed to pause session', 'error');
    }
  };

  const handleEnd = async () => {
    if (!id) return;
    setEndDialogOpen(false);
    try {
      await sessionService.endSession(id);
      showNotification('Session ended', 'success');
      await loadData();
    } catch {
      showNotification('Failed to end session', 'error');
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    setDeleteDialogOpen(false);
    try {
      await sessionService.deleteSession(id);
      showNotification('Session deleted', 'success');
      navigate('/instructor/sessions');
    } catch {
      showNotification('Failed to delete session', 'error');
    }
  };

  const handleOpenAddCoInstructor = async () => {
    setAddCoInstructorDialogOpen(true);
    try {
      const all = await sessionService.getInstructors();
      const existing = session?.coInstructors?.map((ci: any) => ci.instructorId) || [];
      setAvailableInstructors(all.filter((i) =>
        i.id !== user?.id &&
        i.id !== session?.workshop?.instructorId &&
        !existing.includes(i.id),
      ));
    } catch {
      setAvailableInstructors([]);
    }
  };

  const handleAddCoInstructor = async () => {
    if (!id || !selectedNewCoInstructor) return;
    setAddingCoInstructor(true);
    try {
      await sessionService.addCoInstructors(id, [selectedNewCoInstructor.id]);
      showNotification('Co-instructor added', 'success');
      setAddCoInstructorDialogOpen(false);
      setSelectedNewCoInstructor(null);
      await loadData();
    } catch {
      showNotification('Failed to add co-instructor', 'error');
    } finally {
      setAddingCoInstructor(false);
    }
  };

  const handleRemoveCoInstructor = async (instructorId: string) => {
    if (!id) return;
    try {
      await sessionService.removeCoInstructor(id, instructorId);
      showNotification('Co-instructor removed', 'success');
      await loadData();
    } catch {
      showNotification('Failed to remove co-instructor', 'error');
    }
  };

  const handleSaveSettings = async () => {
    if (!id) return;
    setSavingSettings(true);
    try {
      await sessionService.updateSession(id, settingsForm);
      showNotification('Settings saved', 'success');
      await loadData();
    } catch {
      showNotification('Failed to save settings', 'error');
    } finally {
      setSavingSettings(false);
    }
  };

  // FL11: Broadcast message
  const handleBroadcast = () => {
    if (!id || !broadcastMsg.trim()) return;
    if (connectionStatus !== 'connected') {
      showNotification('Cannot broadcast — not connected to session', 'warning');
      return;
    }
    socketService.sendBroadcast(id, broadcastMsg.trim());
    setBroadcastDialogOpen(false);
    setBroadcastMsg('');
    showNotification('Message broadcast to all students', 'success');
  };

  // Send invitation emails
  const handleSendInvitations = async () => {
    if (!id) return;
    setInviteDialogOpen(false);
    setSendingInvitations(true);
    try {
      const result = await sessionService.sendInvitations(id);
      if (result.failed.length === 0) {
        showNotification(`Invitations sent to ${result.sent.length} student(s)`, 'success');
      } else {
        showNotification(
          `Sent to ${result.sent.length}, failed for ${result.failed.length} student(s)`,
          result.sent.length > 0 ? 'warning' : 'error',
        );
      }
    } catch {
      showNotification('Failed to send invitations', 'error');
    } finally {
      setSendingInvitations(false);
    }
  };

  // FL11: Add students
  const handleAddStudent = async () => {
    if (!id || !newStudentName.trim() || !newStudentEmail.trim()) return;
    setAddingStudent(true);
    try {
      const studentInput: AddStudentInput[] = [{
        name: newStudentName.trim(),
        email: newStudentEmail.trim(),
        tenantUrl: newStudentTenantUrl || undefined,
        tenantAdminEmail: newStudentTenantAdminEmail || undefined,
        tenantAdminPassword: newStudentTenantAdminPassword || undefined,
        sharingPortalUrl: newStudentSharingPortalUrl || undefined,
        skytapPortalPassword: newStudentSkytapPortalPassword || undefined,
      }];
      await sessionService.addStudents(id, studentInput);
      showNotification('Student added', 'success');
      setAddStudentsDialogOpen(false);
      setNewStudentName('');
      setNewStudentEmail('');
      setNewStudentTenantUrl('');
      setNewStudentTenantAdminEmail('');
      setNewStudentTenantAdminPassword('');
      setNewStudentSharingPortalUrl('');
      setNewStudentSkytapPortalPassword('');
      await loadData();
    } catch {
      showNotification('Failed to add student', 'error');
    } finally {
      setAddingStudent(false);
    }
  };

  // FL11: Reset credentials
  const handleResetCredentials = async () => {
    if (!id || !selectedStudentForReset) return;
    try {
      await sessionService.updateStudentCredentials(id, selectedStudentForReset.id, {
        tenantUrl: newTenantUrl || undefined,
        tenantAdminEmail: newTenantAdminEmail || undefined,
        tenantAdminPassword: newTenantAdminPassword || undefined,
        sharingPortalUrl: newSharingPortalUrl || undefined,
        skytapPortalPassword: newSkytapPortalPassword || undefined,
      });
      showNotification('Credentials updated', 'success');
      setResetCredDialogOpen(false);
      setSelectedStudentForReset(null);
      setNewTenantUrl('');
      setNewTenantAdminEmail('');
      setNewTenantAdminPassword('');
      setNewSharingPortalUrl('');
      setNewSkytapPortalPassword('');
      await loadData();
    } catch {
      showNotification('Failed to update credentials', 'error');
    }
  };

  // FL11: Export student list as CSV
  const handleExportStudentList = () => {
    if (students.length === 0) {
      showNotification('No students to export', 'info');
      return;
    }
    const header = 'Name,Email,Joined At';
    const rows = students.map((s) => `"${s.firstName} ${s.lastName}","${s.email}","${s.assignedAt}"`);
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `students-${session?.name || 'session'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    showNotification('Student list exported', 'success');
  };

  // FL11: Export progress data as CSV
  const handleExportProgressData = async () => {
    if (!id) return;
    try {
      const overviewData = await progressService.getSessionOverview(id);
      if (overviewData.length === 0) {
        showNotification('No progress data to export', 'info');
        return;
      }
      const header = 'Student Name,Completed Steps,Total Steps,Progress %,Current Step';
      const rows = overviewData.map((o) =>
        `"${o.studentName}",${o.completedSteps},${o.totalSteps},${o.progressPercent},"${o.currentStepTitle || 'N/A'}"`,
      );
      const csv = [header, ...rows].join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `progress-${session?.name || 'session'}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      showNotification('Progress data exported', 'success');
    } catch {
      showNotification('Failed to export progress data', 'error');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!session) return null;

  const isActive = session.status === 'active';
  const isScheduled = session.status === 'scheduled';
  const isPreparing = session.status === 'preparing' || preparing;
  const isReady = session.status === 'ready';
  const isCompleted = session.status === 'completed';
  const isPreLive = ['scheduled', 'preparing', 'ready'].includes(session.status);
  const isOwner = session.workshop?.instructorId === user?.id;

  const getInitials = (first: string, last: string) =>
    `${first?.[0] || ''}${last?.[0] || ''}`.toUpperCase();

  const stringToColor = (str: string) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
    return `hsl(${hash % 360}, 55%, 50%)`;
  };

  // --- Tab: Students ---
  const renderStudentsTab = () => (
    <Box>
      {students.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">No students assigned to this session yet.</Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {students.map((s) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={s.id}>
              <Paper sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                  <Avatar sx={{ backgroundColor: stringToColor(s.email), width: 40, height: 40 }}>
                    {getInitials(s.firstName, s.lastName)}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {s.firstName} {s.lastName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {s.email}
                    </Typography>
                    {s.tempPassword?.startsWith('(self-registered') && (
                      <Chip label="Self-registered" size="small" color="info" variant="outlined" sx={{ mt: 0.5, height: 20, fontSize: '0.6875rem' }} />
                    )}
                    {s.tempPassword === '(use existing password)' && (
                      <Chip label="Existing account" size="small" color="info" variant="outlined" sx={{ mt: 0.5, height: 20, fontSize: '0.6875rem' }} />
                    )}
                  </Box>
                </Box>
                {(() => {
                  const ov = overview.get(s.userId);
                  const pct = ov?.progressPercent ?? 0;
                  const completed = ov?.completedSteps ?? 0;
                  const total = ov?.totalSteps ?? 0;
                  const label = completed === total && total > 0
                    ? 'Completed'
                    : completed > 0
                      ? `${completed} / ${total} steps`
                      : 'Not Started';
                  return (
                    <>
                      <LinearProgress variant="determinate" value={pct} sx={{ mb: 0.5, height: 6, borderRadius: 3 }} />
                      <Typography variant="caption" color="text.secondary">{label}</Typography>
                    </>
                  );
                })()}
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );

  // --- Tab: Activity Timeline ---
  const renderTimelineTab = () => {
    const events = [
      { time: dayjs(session.createdAt).format('MMM D, h:mm A'), title: 'Session Created', description: `Session "${session.name}" was created`, color: '#4caf50' },
      { time: dayjs(session.createdAt).format('MMM D, h:mm A'), title: 'Email Invitations Scheduled', description: `${students.length} student(s) will receive invitations`, color: '#2196f3' },
      ...(session.actualStart ? [{ time: dayjs(session.actualStart).format('MMM D, h:mm A'), title: 'Session Started', description: 'Instructor started the session', color: '#4caf50' }] : []),
      ...(session.endedAt ? [{ time: dayjs(session.endedAt).format('MMM D, h:mm A'), title: 'Session Ended', description: 'Session was completed', color: '#9e9e9e' }] : []),
      ...(!session.actualStart ? [{ time: '—', title: 'Session Starts', description: session.scheduledStart ? `Scheduled for ${dayjs(session.scheduledStart).format('MMM D, h:mm A')}` : 'Not yet scheduled', color: '#e2e8f0' }] : []),
    ];

    return (
      <Box sx={{ maxWidth: 600 }}>
        {events.map((evt, i) => (
          <Box key={i} sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <DotIcon sx={{ color: evt.color, fontSize: 16 }} />
              {i < events.length - 1 && (
                <Box sx={{ width: 2, flex: 1, backgroundColor: 'divider', my: 0.5 }} />
              )}
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">{evt.time}</Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{evt.title}</Typography>
              <Typography variant="caption" color="text.secondary">{evt.description}</Typography>
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  // --- Tab: Session Controls ---
  const renderControlsTab = () => (
    <Box>
      {/* Session State */}
      <Typography variant="subtitle2" sx={{ mb: 2 }}>Session State</Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 4, alignItems: 'center' }}>
        {isScheduled && !isPreparing && (
          <Tooltip title="Provision desktops for all enrolled students">
            <Button variant="contained" color="primary" onClick={handlePrepare}>
              Prepare Session
            </Button>
          </Tooltip>
        )}
        {isPreparing && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary">
              Provisioning desktops for {students.length} student(s)...
            </Typography>
          </Box>
        )}
        {isReady && !isPreparing && (
          <>
            <Button variant="contained" color="success" startIcon={<StartIcon />} onClick={handleStart}>
              Start Session
            </Button>
            <Chip
              label={`${students.length + 1} desktop(s) provisioned and ready`}
              color="success"
              variant="outlined"
              sx={{ ml: 1 }}
            />
          </>
        )}
        {isActive && (
          <>
            <Button variant="outlined" color="warning" startIcon={<PauseIcon />} onClick={handlePause}>
              Pause Session
            </Button>
            <Tooltip title="Requires session time limits (not yet configured)">
              <span>
                <Button variant="outlined" startIcon={<TimerIcon />} disabled>
                  Extend Time
                </Button>
              </span>
            </Tooltip>
            <Button variant="contained" color="error" startIcon={<StopIcon />} onClick={() => setEndDialogOpen(true)}>
              End Session
            </Button>
          </>
        )}
        {isCompleted && (
          <>
            <Chip label="Session has ended" color="default" />
            {(() => {
              const graceDesktops = desktopStatuses.filter((d: any) => d.status === 'grace');
              const allDestroyed = desktopStatuses.length > 0 && desktopStatuses.every((d: any) => d.status === 'destroyed');
              if (graceDesktops.length > 0) {
                const expiresAt = graceDesktops[0].ttlExpiresAt
                  ? new Date(graceDesktops[0].ttlExpiresAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
                  : 'N/A';
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="body2" color="text.secondary">
                      {graceDesktops.length} desktop(s) in grace period (expires {expiresAt})
                    </Typography>
                    <Button
                      variant="contained"
                      color="error"
                      size="small"
                      onClick={() => setCleanupDialogOpen(true)}
                      disabled={cleaningUp}
                    >
                      {cleaningUp ? <CircularProgress size={18} color="inherit" /> : 'Clean Up Now'}
                    </Button>
                  </Box>
                );
              }
              if (allDestroyed) {
                return (
                  <Typography variant="body2" color="text.secondary">
                    All desktops have been cleaned up
                  </Typography>
                );
              }
              return null;
            })()}
          </>
        )}
      </Box>

      {/* Communication */}
      <Typography variant="subtitle2" sx={{ mb: 2 }}>Communication</Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 4 }}>
        <Tooltip title={isCompleted ? 'Session has ended' : ''}>
          <span>
            <Button variant="outlined" startIcon={<BroadcastIcon />} onClick={() => setBroadcastDialogOpen(true)} disabled={isCompleted}>
              Broadcast Message
            </Button>
          </span>
        </Tooltip>
        <Tooltip title={isCompleted ? 'Session has ended' : ''}>
          <span>
            <Button
              variant="outlined"
              startIcon={sendingInvitations ? <CircularProgress size={18} color="inherit" /> : <EmailIcon />}
              onClick={() => setInviteDialogOpen(true)}
              disabled={isCompleted || sendingInvitations || students.length === 0}
            >
              Send Invitations
            </Button>
          </span>
        </Tooltip>
      </Box>

      {/* Co-Instructors */}
      <Typography variant="subtitle2" sx={{ mb: 2 }}>Co-Instructors</Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        {session.coInstructors && session.coInstructors.length > 0 ? (
          session.coInstructors.map((ci: any) => (
            <Chip
              key={ci.id}
              label={`${ci.instructor?.firstName || ''} ${ci.instructor?.lastName || ''}`.trim() || ci.instructorId}
              onDelete={isPreLive && isOwner ? () => handleRemoveCoInstructor(ci.instructorId) : undefined}
              size="small"
              variant="outlined"
            />
          ))
        ) : (
          <Typography variant="body2" color="text.secondary">No co-instructors assigned</Typography>
        )}
      </Box>
      {isPreLive && isOwner && (
        <Button
          variant="outlined"
          size="small"
          startIcon={<PersonAddIcon />}
          onClick={handleOpenAddCoInstructor}
          sx={{ mb: 4 }}
        >
          Add Co-Instructor
        </Button>
      )}
      {(!isPreLive || !isOwner) && <Box sx={{ mb: 4 }} />}

      {/* Student Management */}
      <Typography variant="subtitle2" sx={{ mb: 2 }}>Student Management</Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 4 }}>
        <Tooltip title={isCompleted ? 'Session has ended' : ''}>
          <span>
            <Button variant="outlined" startIcon={<AddStudentIcon />} onClick={() => setAddStudentsDialogOpen(true)} disabled={isCompleted}>
              Add Students
            </Button>
          </span>
        </Tooltip>
        <Button variant="outlined" startIcon={<ExportIcon />} onClick={handleExportStudentList}>
          Export Student List
        </Button>
        <Button
          variant="outlined"
          startIcon={<CredentialsIcon />}
          onClick={() => {
            if (students.length === 0) {
              showNotification('No students in this session', 'info');
              return;
            }
            const first = students[0];
            setSelectedStudentForReset(first);
            setNewTenantUrl(first.tenantUrl || '');
            setNewTenantAdminEmail(first.tenantAdminEmail || '');
            setNewTenantAdminPassword(first.tenantAdminPassword || '');
            setNewSharingPortalUrl(first.sharingPortalUrl || '');
            setNewSkytapPortalPassword(first.skytapPortalPassword || '');
            setResetCredDialogOpen(true);
          }}
        >
          Reset Credentials
        </Button>
      </Box>

      {/* Session Data */}
      <Typography variant="subtitle2" sx={{ mb: 2 }}>Session Data</Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        <Button variant="outlined" startIcon={<ExportIcon />} onClick={handleExportProgressData}>
          Export Progress Data
        </Button>
        {isOwner && isScheduled && (
          <Button variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={() => setDeleteDialogOpen(true)}>
            Delete Session
          </Button>
        )}
      </Box>
    </Box>
  );

  // --- Tab: Settings ---
  const renderSettingsTab = () => (
    <Box sx={{ maxWidth: 600 }}>
      <FormControlLabel
        control={<Checkbox checked={settingsForm.enableProgressTracking} onChange={(e) => setSettingsForm((p) => ({ ...p, enableProgressTracking: e.target.checked }))} />}
        label="Enable real-time progress tracking"
        disabled={isCompleted}
      />
      <FormControlLabel
        control={<Checkbox checked={settingsForm.allowHelpRequests} onChange={(e) => setSettingsForm((p) => ({ ...p, allowHelpRequests: e.target.checked }))} />}
        label="Allow help requests"
        disabled={isCompleted}
      />
      <FormControlLabel
        control={<Checkbox checked={settingsForm.sendReminders} onChange={(e) => setSettingsForm((p) => ({ ...p, sendReminders: e.target.checked }))} />}
        label="Send automated reminders"
        disabled={isCompleted}
      />
      <FormControlLabel
        control={<Checkbox checked={settingsForm.recordSession} onChange={(e) => setSettingsForm((p) => ({ ...p, recordSession: e.target.checked }))} />}
        label="Record session for later review"
        disabled={isCompleted}
      />
      {!isCompleted && (
        <>
          <Divider sx={{ my: 2 }} />
          <Button
            variant="contained"
            onClick={handleSaveSettings}
            disabled={savingSettings}
          >
            {savingSettings ? <CircularProgress size={24} color="inherit" /> : 'Save Settings'}
          </Button>
        </>
      )}
    </Box>
  );

  return (
    <Box>
      {/* Session Banner */}
      <Box
        sx={{
          mx: -3,
          mt: -2,
          px: 4,
          py: 3,
          mb: 3,
          background: (theme: any) => isActive
            ? `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`
            : isReady
            ? `linear-gradient(135deg, ${theme.palette.success.light} 0%, ${theme.palette.success.main} 100%)`
            : isPreparing
            ? `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${theme.palette.warning.dark} 100%)`
            : isScheduled
            ? `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.dark} 100%)`
            : `linear-gradient(135deg, ${theme.palette.grey[700]} 0%, ${theme.palette.grey[800]} 100%)`,
          color: 'white',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              {session.name}
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              {session.workshop?.title || 'Tech Eval'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {isOwner && (
              <Button
                variant="contained"
                startIcon={<PresentToAllIcon />}
                onClick={() => navigate(`/instructor/session/${id}/present`)}
                sx={{
                  fontWeight: 600,
                  bgcolor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                }}
              >
                Present
              </Button>
            )}
            <Chip
              icon={isActive ? <DotIcon sx={{ color: '#4caf50 !important', animation: 'pulse 2s infinite', '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.3 } } }} /> : undefined}
              label={statusConfig[session.status]?.label || session.status}
              sx={{
                backgroundColor: 'rgba(255,255,255,0.2)',
                color: 'white',
                fontWeight: 'bold',
              }}
            />
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 3, mt: 1, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <ScheduleIcon sx={{ fontSize: 16 }} />
            <Typography variant="body2">
              {session.scheduledStart ? dayjs(session.scheduledStart).format('MMM D, YYYY h:mm A') : 'Not scheduled'}
            </Typography>
          </Box>
          <Typography variant="body2">{students.length} student(s)</Typography>
          <Typography variant="body2">{session.duration} min</Typography>
        </Box>
      </Box>

      {/* Quick Actions */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Paper
            sx={{ p: 2, textAlign: 'center', cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
            onClick={() => navigate(`/instructor/live/${id}`)}
          >
            <MonitorIcon sx={{ fontSize: 32, color: 'primary.main', mb: 0.5 }} />
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Start Monitoring</Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Paper
            sx={{ p: 2, textAlign: 'center', cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
            onClick={() => setBroadcastDialogOpen(true)}
          >
            <BroadcastIcon sx={{ fontSize: 32, color: 'warning.main', mb: 0.5 }} />
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Broadcast Message</Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Paper
            sx={{
              p: 2,
              textAlign: 'center',
              cursor: students.length > 0 && !sendingInvitations ? 'pointer' : 'default',
              opacity: students.length > 0 ? 1 : 0.5,
              '&:hover': students.length > 0 ? { backgroundColor: 'action.hover' } : {},
            }}
            onClick={() => {
              if (students.length > 0 && !sendingInvitations) setInviteDialogOpen(true);
            }}
          >
            {sendingInvitations
              ? <CircularProgress size={32} sx={{ mb: 0.5 }} />
              : <EmailIcon sx={{ fontSize: 32, color: 'info.main', mb: 0.5 }} />
            }
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              {sendingInvitations ? 'Sending...' : 'Send Invitations'}
            </Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Paper
            sx={{ p: 2, textAlign: 'center', cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
            onClick={() => navigate(`/instructor/session/${id}/summary`)}
          >
            <AnalyticsIcon sx={{ fontSize: 32, color: 'success.main', mb: 0.5 }} />
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>View Analytics</Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Tabbed Content */}
      <Paper>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label={`Students (${students.length})`} />
          <Tab label="Activity Timeline" />
          <Tab label="Session Controls" />
          <Tab label="Settings" />
        </Tabs>
        <Box sx={{ p: 3 }}>
          {activeTab === 0 && renderStudentsTab()}
          {activeTab === 1 && renderTimelineTab()}
          {activeTab === 2 && renderControlsTab()}
          {activeTab === 3 && renderSettingsTab()}
        </Box>
      </Paper>

      {/* End Session Dialog */}
      <Dialog open={endDialogOpen} onClose={() => setEndDialogOpen(false)}>
        <DialogTitle>End Session</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will end the session for all students. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEndDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleEnd} color="error" variant="contained">End Session</Button>
        </DialogActions>
      </Dialog>

      {/* Add Co-Instructor Dialog */}
      <Dialog open={addCoInstructorDialogOpen} onClose={() => setAddCoInstructorDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Co-Instructor</DialogTitle>
        <DialogContent>
          <Autocomplete
            options={availableInstructors}
            getOptionLabel={(o) => `${o.firstName} ${o.lastName} (${o.email})`}
            value={selectedNewCoInstructor}
            onChange={(_, v) => setSelectedNewCoInstructor(v)}
            renderInput={(params) => <TextField {...params} label="Search instructors" margin="dense" />}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddCoInstructorDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddCoInstructor} disabled={!selectedNewCoInstructor || addingCoInstructor} variant="contained">
            {addingCoInstructor ? <CircularProgress size={18} /> : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Session Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Session</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently delete this session and remove all student assignments. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Broadcast Message Dialog */}
      <Dialog open={broadcastDialogOpen} onClose={() => setBroadcastDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Broadcast Message</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Send a message to all students in this session.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={3}
            label="Message"
            value={broadcastMsg}
            onChange={(e) => setBroadcastMsg(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBroadcastDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleBroadcast} variant="contained" disabled={!broadcastMsg.trim()}>
            Send
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clean Up Desktops Dialog */}
      <Dialog open={cleanupDialogOpen} onClose={() => setCleanupDialogOpen(false)} maxWidth="xs">
        <DialogTitle>Clean Up Desktops</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will immediately destroy all student desktops. Students will lose access to their practice environments. Continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCleanupDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCleanup} color="error" variant="contained">Confirm</Button>
        </DialogActions>
      </Dialog>

      {/* Send Invitations Dialog */}
      <Dialog open={inviteDialogOpen} onClose={() => setInviteDialogOpen(false)} maxWidth="xs">
        <DialogTitle>Send Invitation Emails</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Send invitation emails with credentials to <strong>{students.length}</strong> student(s) in <strong>{session.name}</strong>?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSendInvitations} variant="contained" color="primary">
            Send Invitations
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Students Dialog */}
      <Dialog open={addStudentsDialogOpen} onClose={() => setAddStudentsDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Student</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Add a new student to this session.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            label="Full Name"
            value={newStudentName}
            onChange={(e) => setNewStudentName(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={newStudentEmail}
            onChange={(e) => setNewStudentEmail(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, mb: 1, display: 'block' }}>
            Environment Credentials (Optional)
          </Typography>
          <TextField
            fullWidth
            label="Tenant URL"
            value={newStudentTenantUrl}
            onChange={(e) => setNewStudentTenantUrl(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Tenant Admin Email"
                value={newStudentTenantAdminEmail}
                onChange={(e) => setNewStudentTenantAdminEmail(e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Tenant Admin Password"
                value={newStudentTenantAdminPassword}
                onChange={(e) => setNewStudentTenantAdminPassword(e.target.value)}
                slotProps={{ input: { sx: { fontFamily: 'monospace' } } }}
              />
            </Grid>
          </Grid>
          <TextField
            fullWidth
            label="Sharing Portal URL"
            value={newStudentSharingPortalUrl}
            onChange={(e) => setNewStudentSharingPortalUrl(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Skytap Portal Password"
            value={newStudentSkytapPortalPassword}
            onChange={(e) => setNewStudentSkytapPortalPassword(e.target.value)}
            slotProps={{ input: { sx: { fontFamily: 'monospace' } } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddStudentsDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleAddStudent}
            variant="contained"
            disabled={addingStudent || !newStudentName.trim() || !newStudentEmail.trim()}
          >
            {addingStudent ? <CircularProgress size={20} color="inherit" /> : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Credentials Dialog */}
      <Dialog open={resetCredDialogOpen} onClose={() => setResetCredDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reset Credentials</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Update credentials for a student.
          </DialogContentText>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Student</InputLabel>
            <Select
              value={selectedStudentForReset?.userId || ''}
              label="Student"
              onChange={(e) => {
                const student = students.find((s) => s.userId === e.target.value);
                if (student) {
                  setSelectedStudentForReset(student);
                  setNewTenantUrl(student.tenantUrl || '');
                  setNewTenantAdminEmail(student.tenantAdminEmail || '');
                  setNewTenantAdminPassword(student.tenantAdminPassword || '');
                  setNewSharingPortalUrl(student.sharingPortalUrl || '');
                  setNewSkytapPortalPassword(student.skytapPortalPassword || '');
                }
              }}
            >
              {students.map((s) => (
                <MenuItem key={s.userId} value={s.userId}>
                  {s.firstName} {s.lastName} ({s.email})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            Environment Credentials
          </Typography>
          <TextField
            fullWidth
            label="Tenant URL"
            value={newTenantUrl}
            onChange={(e) => setNewTenantUrl(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Tenant Admin Email"
                value={newTenantAdminEmail}
                onChange={(e) => setNewTenantAdminEmail(e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Tenant Admin Password"
                value={newTenantAdminPassword}
                onChange={(e) => setNewTenantAdminPassword(e.target.value)}
                slotProps={{ input: { sx: { fontFamily: 'monospace' } } }}
              />
            </Grid>
          </Grid>
          <TextField
            fullWidth
            label="Sharing Portal URL"
            value={newSharingPortalUrl}
            onChange={(e) => setNewSharingPortalUrl(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Skytap Portal Password"
            value={newSkytapPortalPassword}
            onChange={(e) => setNewSkytapPortalPassword(e.target.value)}
            slotProps={{ input: { sx: { fontFamily: 'monospace' } } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetCredDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleResetCredentials}
            variant="contained"
            disabled={!selectedStudentForReset}
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SessionActive;
